import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Heading,
  Alert,
  AlertIcon,
  Divider,
  Container,
  useToast,
  InputGroup,
  InputRightElement
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useForm } from 'react-hook-form';
import { signInUser, createUser, createUserProfile, getFriendlyErrorMessage } from '../utils/firebaseUtils';
// import { useAuth } from '../contexts/AuthContext';
// import type { UserProfile } from '../utils/firebaseUtils'
import { hasUserRole } from '../utils/userUtils'
const { getUserProfile, signOutUser } = await import('../utils/firebaseUtils');

interface LoginFormData {
  email: string;
  password: string;
}

const LoginScreen: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  // const { isDemoMode } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');
    setVerifying(false);

    try {
      let userCredential;
      if (isSignUp) {
        userCredential = await createUser(data.email, data.password);
        await createUserProfile(userCredential.user.uid, {
          email: data.email,
          name: data.email.split('@')[0],
          role: 'user'
        });
        toast({
          title: 'Account created successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        userCredential = await signInUser(data.email, data.password);
      }

      setIsLoading(false);
      setVerifying(true);

      // Fetch user profile and check role
      const profileResult = await getUserProfile(userCredential.user.uid);
      if (!profileResult) {
        await signOutUser();
        setVerifying(false);
        toast({
          title: 'Access Denied',
          description: 'User profile not found. You are not authorized to access the mobile app.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }
      if (!profileResult.uid) {
        await signOutUser();
        setVerifying(false);
        toast({
          title: 'Access Denied',
          description: 'User ID not found. You are not authorized to access the mobile app.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      const hasValidRole = await hasUserRole(profileResult.uid);
      if (!hasValidRole) {
        await signOutUser();
        setVerifying(false);
        toast({
          title: 'Access Denied',
          description: 'You are not authorized to access the mobile app.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }
      if (profileResult.active === false) {
        await signOutUser();
        setVerifying(false);
        toast({
          title: 'Account Inactive',
          description: 'Your account is not active. Please contact support.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      setVerifying(false);
      if (!isSignUp) {
        toast({
          title: 'Welcome back!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      navigate('/dashboard');
    } catch (error) {
      console.log({error})
      setVerifying(false);
      const friendlyMessage = getFriendlyErrorMessage(error);
      setError(friendlyMessage);
      // Show additional toast for account deactivation
      if ((error as any)?.code === 'ACCOUNT_DEACTIVATED_FAILED_ATTEMPTS') {
        toast({
          title: 'Account Deactivated',
          description: 'Please contact admin support to reactivate your account.',
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // const handleGoogleSignIn = async () => {
  //   setIsLoading(true);
  //   setError('');
  //   setVerifying(false);

  //   try {
  //     const result = await signInWithGoogle();
  //     const user = result.user;
  //     const { getUserProfile, createUserProfile, signOutUser } = await import('../utils/firebaseUtils');
  //     let profile = await getUserProfile(user.uid);
  //     if (!profile) {
  //       // Create user profile if it's a new user
  //       profile = await createUserProfile(user.uid, {
  //         email: user.email || '',
  //         name: user.displayName || user.email?.split('@')[0],
  //         role: 'user'
  //       });
  //     }

  //     setIsLoading(false);
  //     setVerifying(true);

  //     if (profile.role !== 'user') {
  //       await signOutUser();
  //       setVerifying(false);
  //       toast({
  //         title: 'Access Denied',
  //         description: 'You are not authorized to access the mobile app.',
  //         status: 'error',
  //         duration: 6000,
  //         isClosable: true,
  //       });
  //       return;
  //     }
  //     if (profile.active === false) {
  //       await signOutUser();
  //       setVerifying(false);
  //       toast({
  //         title: 'Account Inactive',
  //         description: 'Your account is not active. Please contact support.',
  //         status: 'error',
  //         duration: 6000,
  //         isClosable: true,
  //       });
  //       return;
  //     }

  //     setVerifying(false);
  //     toast({
  //       title: 'Welcome!',
  //       status: 'success',
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //     navigate('/dashboard');
  //   } catch (error) {
  //     setVerifying(false);
  //     const friendlyMessage = getFriendlyErrorMessage(error);
  //     setError(friendlyMessage);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    reset();
  };

  if (verifying) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
        <VStack spacing={6}>
          <Heading size="lg" color="brand.700">Verifying your account...</Heading>
          <Text color="gray.600">Please wait while we check your access.</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box 
      minH="100vh" 
      bg="gray.50" 
      py={{ base: 4, md: 8 }} 
      px={{ base: 2, md: 0 }}
      w="100%"
      overflowX="hidden"
    >
      <Container maxW="md" px={{ base: 4, md: 6 }} w="100%">
        <Box
          bg="white"
          p={{ base: 4, md: 8 }}
          borderRadius="xl"
          boxShadow="xl"
          border="1px"
          borderColor="gray.200"
          w="100%"
          maxW="100%"
        >
          <VStack spacing={{ base: 5, md: 6 }} w="100%">
            <Heading
              size={{ base: "lg", md: "xl" }}
              textAlign="center"
              color="brand.700"
              fontWeight="700"
            >
              {isSignUp ? 'Create Account' : 'Welcome Back'}
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              textAlign="center"
              color="gray.600"
              maxW="sm"
              px={2}
            >
              {isSignUp
                ? 'Sign up to access your reports and manage your profile'
                : 'Sign in to access your reports and dashboard'
              }
            </Text>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Text fontSize="md">{error}</Text>
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              <VStack spacing={4} w="100%">
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>Email Address</FormLabel>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                  {errors.email && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.email.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl isInvalid={!!errors.password}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                    />
                    <InputRightElement h="full">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword((show) => !show)}
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  {errors.password && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.password.message}
                    </Text>
                  )}
                </FormControl>

                <Button
                  type="submit"
                  size={{ base: "lg", md: "xl" }}
                  w="full"
                  isLoading={isLoading}
                  loadingText={isSignUp ? 'Creating Account...' : 'Signing In...'}
                  mt={4}
                  isDisabled={false}
                >
                  {isSignUp ? 'Create Account' : 'Sign In'}
                </Button>
              </VStack>
            </form>

            <Divider />

            {/* <Button
              onClick={handleGoogleSignIn}
              size={{ base: "lg", md: "xl" }}
              w="full"
              variant="outline"
              isLoading={isLoading}
              loadingText="Signing in with Google..."
              isDisabled={false}
            >
              Continue with Google
            </Button> */}

            <Text textAlign="center" color="gray.600" fontSize={{ base: "md", md: "lg" }}>
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <Button
                variant="link"
                color="brand.500"
                onClick={toggleMode}
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="600"
              >
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </Button>
            </Text>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginScreen;
